import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useState } from 'react';
import { useScrollReveal } from '@/hooks/useScrollReveal';

const FORM_URL = 'https://readdy.ai/api/form/d8g2i83tvf9bji89p2gg';

export default function ApplyPage() {
  const { ref: heroRef, isVisible: heroVisible } = useScrollReveal(0.1);
  const { ref: formRef, isVisible: formVisible } = useScrollReveal(0.1);

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('company_alt') as string || '').trim();
    if (honeypot.length > 0) {
      setSubmitted(true);
      setSubmitting(false);
      return;
    }

    const name = (formData.get('name') as string || '').trim();
    const email = (formData.get('email') as string || '').trim();
    const phone = (formData.get('phone') as string || '').trim();

    if (!name || !email || !phone) {
      setError('Please fill in all required fields (name, email, phone).');
      setSubmitting(false);
      return;
    }

    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key !== 'company_alt') {
        params.append(key, value as string);
      }
    });

    try {
      const response = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSuccess = response.ok && parsed?.code === 'OK';

      if (isSuccess) {
        setSubmitted(true);
        form.reset();
      } else {
        setError(serverMsg || 'Something went wrong. Please try again later.');
      }
    } catch {
      setError('Network error. Please check your connection and try again.');
    }

    setSubmitting(false);
  };

  return (
    <main>
      <Navbar />
      <div className="h-20"></div>

      {/* Hero */}
      <section className="relative bg-background-50 py-20 md:py-28 px-6 md:px-10 lg:px-16">
        <div
          ref={heroRef}
          className={`max-w-4xl mx-auto text-center transition-all duration-700 ${
            heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <span className="inline-block px-4 py-1.5 rounded-full bg-accent-100 text-accent-600 text-xs font-bold tracking-widest uppercase mb-6">
            Career Opportunities
          </span>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-foreground-900 leading-[1.1] mb-5">
            Your Next{' '}
            <span className="text-accent-500 italic">Career Move</span>{' '}
            Starts Here
          </h1>
          <p className="text-foreground-500 text-lg md:text-xl leading-relaxed max-w-2xl mx-auto">
            Looking for your next opportunity? We work with top organizations across industries who are actively hiring. Fill out our application form and let us help you find the right fit.
          </p>
        </div>
      </section>

      {/* Form Section */}
      <section className="relative bg-background-50 pb-24 md:pb-32 px-6 md:px-10 lg:px-16">
        <div
          ref={formRef}
          className={`max-w-7xl mx-auto transition-all duration-700 ${
            formVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            {/* Left - Info */}
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
                How It Works
              </h2>
              <p className="text-foreground-500 text-base leading-relaxed mb-8">
                Our team will review your profile and match you with opportunities that align with your skills, experience, and career aspirations.
              </p>

              <div className="space-y-4">
                {[
                  { icon: 'ri-check-line', text: 'Tell us about your experience and skills' },
                  { icon: 'ri-check-line', text: 'Upload your latest resume/CV' },
                  { icon: 'ri-check-line', text: 'Specify your industry preferences' },
                  { icon: 'ri-check-line', text: 'Share your career aspirations' },
                  { icon: 'ri-check-line', text: 'Our team matches you with opportunities' },
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <span className="w-6 h-6 flex items-center justify-center rounded-full bg-accent-100 flex-shrink-0">
                      <i className={`${item.icon} text-accent-500 text-xs`}></i>
                    </span>
                    <span className="text-foreground-600 text-sm">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right - Form */}
            <div>
              {submitted ? (
                <div className="rounded-3xl bg-background-100 border border-background-200 p-10 md:p-12 text-center">
                  <div className="w-20 h-20 mx-auto rounded-full bg-accent-100 flex items-center justify-center mb-6">
                    <i className="ri-check-line text-accent-500 text-3xl"></i>
                  </div>
                  <h3 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                    Application Submitted!
                  </h3>
                  <p className="text-foreground-500 text-sm leading-relaxed mb-6">
                    Thank you for your interest! Our team will review your profile and get back to you within 2-3 business days.
                  </p>
                  <button
                    onClick={() => setSubmitted(false)}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full border-2 border-accent-500 text-accent-600 text-sm font-semibold whitespace-nowrap hover:bg-accent-50 transition-all duration-300 cursor-pointer"
                  >
                    Submit Another Application
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  data-readdy-form
                  id="apply-form"
                  className="rounded-3xl bg-background-100 border border-background-200 p-8 md:p-10"
                >
                  <div className="flex items-center gap-4 mb-8">
                    <div className="w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-accent-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-briefcase-line text-accent-500 text-2xl"></i>
                    </div>
                    <div>
                      <div className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
                        Apply Now
                      </div>
                      <div className="text-foreground-500 text-sm">
                        All fields marked with * are required
                      </div>
                    </div>
                  </div>

                  <div className="space-y-5">
                    {/* Honeypot */}
                    <input
                      type="text"
                      name="company_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="sr-only-apply"
                    />

                    {/* Full Name */}
                    <div>
                      <label
                        htmlFor="app-name"
                        className="block text-foreground-700 text-xs font-semibold tracking-wide uppercase mb-2"
                      >
                        Full Name *
                      </label>
                      <input
                        id="app-name"
                        name="name"
                        type="text"
                        required
                        placeholder="Enter your full name"
                        className="w-full px-4 py-3 rounded-xl bg-background-50 border border-background-200 text-foreground-900 text-sm placeholder:text-foreground-400 outline-none focus:border-accent-400 focus:ring-2 focus:ring-accent-100 transition-all"
                      />
                    </div>

                    {/* Email */}
                    <div>
                      <label
                        htmlFor="app-email"
                        className="block text-foreground-700 text-xs font-semibold tracking-wide uppercase mb-2"
                      >
                        Email Address *
                      </label>
                      <input
                        id="app-email"
                        name="email"
                        type="email"
                        required
                        placeholder="you@example.com"
                        className="w-full px-4 py-3 rounded-xl bg-background-50 border border-background-200 text-foreground-900 text-sm placeholder:text-foreground-400 outline-none focus:border-accent-400 focus:ring-2 focus:ring-accent-100 transition-all"
                      />
                    </div>

                    {/* Phone */}
                    <div>
                      <label
                        htmlFor="app-phone"
                        className="block text-foreground-700 text-xs font-semibold tracking-wide uppercase mb-2"
                      >
                        Phone Number *
                      </label>
                      <input
                        id="app-phone"
                        name="phone"
                        type="tel"
                        required
                        placeholder="+91 9876543210"
                        className="w-full px-4 py-3 rounded-xl bg-background-50 border border-background-200 text-foreground-900 text-sm placeholder:text-foreground-400 outline-none focus:border-accent-400 focus:ring-2 focus:ring-accent-100 transition-all"
                      />
                    </div>

                    {/* Current Role */}
                    <div>
                      <label
                        htmlFor="app-role"
                        className="block text-foreground-700 text-xs font-semibold tracking-wide uppercase mb-2"
                      >
                        Current / Last Role
                      </label>
                      <input
                        id="app-role"
                        name="current_role"
                        type="text"
                        placeholder="e.g. Senior Software Engineer"
                        className="w-full px-4 py-3 rounded-xl bg-background-50 border border-background-200 text-foreground-900 text-sm placeholder:text-foreground-400 outline-none focus:border-accent-400 focus:ring-2 focus:ring-accent-100 transition-all"
                      />
                    </div>

                    {/* Experience */}
                    <div>
                      <label
                        htmlFor="app-experience"
                        className="block text-foreground-700 text-xs font-semibold tracking-wide uppercase mb-2"
                      >
                        Total Experience
                      </label>
                      <select
                        id="app-experience"
                        name="experience"
                        className="w-full px-4 py-3 rounded-xl bg-background-50 border border-background-200 text-foreground-900 text-sm outline-none focus:border-accent-400 focus:ring-2 focus:ring-accent-100 transition-all cursor-pointer appearance-none"
                      >
                        <option value="">Select experience</option>
                        <option value="0-1">Fresher (0-1 year)</option>
                        <option value="1-3">1-3 years</option>
                        <option value="3-5">3-5 years</option>
                        <option value="5-10">5-10 years</option>
                        <option value="10-15">10-15 years</option>
                        <option value="15+">15+ years</option>
                      </select>
                    </div>

                    {/* Industry Preference */}
                    <div>
                      <label
                        htmlFor="app-industry"
                        className="block text-foreground-700 text-xs font-semibold tracking-wide uppercase mb-2"
                      >
                        Preferred Industry
                      </label>
                      <select
                        id="app-industry"
                        name="preferred_industry"
                        className="w-full px-4 py-3 rounded-xl bg-background-50 border border-background-200 text-foreground-900 text-sm outline-none focus:border-accent-400 focus:ring-2 focus:ring-accent-100 transition-all cursor-pointer appearance-none"
                      >
                        <option value="">Select industry</option>
                        <option value="automobile">Automobile</option>
                        <option value="ecommerce">E-Commerce</option>
                        <option value="education">Education</option>
                        <option value="manufacturing">Manufacturing</option>
                        <option value="banking">Banking &amp; Insurance</option>
                        <option value="media">Media &amp; Advertising</option>
                        <option value="bpo">BPO &amp; KPO</option>
                        <option value="other">Other</option>
                      </select>
                    </div>

                    {/* Skills */}
                    <div>
                      <label
                        htmlFor="app-skills"
                        className="block text-foreground-700 text-xs font-semibold tracking-wide uppercase mb-2"
                      >
                        Key Skills &amp; Expertise
                      </label>
                      <textarea
                        id="app-skills"
                        name="skills"
                        rows={3}
                        maxLength={500}
                        placeholder="List your key skills, tools, and areas of expertise..."
                        className="w-full px-4 py-3 rounded-xl bg-background-50 border border-background-200 text-foreground-900 text-sm placeholder:text-foreground-400 outline-none focus:border-accent-400 focus:ring-2 focus:ring-accent-100 transition-all resize-none"
                      ></textarea>
                      <p className="text-foreground-400 text-xs mt-1">Max 500 characters</p>
                    </div>

                    {/* Resume Link */}
                    <div>
                      <label
                        htmlFor="app-resume"
                        className="block text-foreground-700 text-xs font-semibold tracking-wide uppercase mb-2"
                      >
                        Resume/CV Link
                      </label>
                      <input
                        id="app-resume"
                        name="resume_link"
                        type="url"
                        placeholder="Google Drive / Dropbox link to your resume"
                        className="w-full px-4 py-3 rounded-xl bg-background-50 border border-background-200 text-foreground-900 text-sm placeholder:text-foreground-400 outline-none focus:border-accent-400 focus:ring-2 focus:ring-accent-100 transition-all"
                      />
                      <p className="text-foreground-400 text-xs mt-1">
                        Upload to Drive/Dropbox and paste the shareable link here
                      </p>
                    </div>

                    {/* Career Aspirations */}
                    <div>
                      <label
                        htmlFor="app-aspirations"
                        className="block text-foreground-700 text-xs font-semibold tracking-wide uppercase mb-2"
                      >
                        Career Aspirations
                      </label>
                      <textarea
                        id="app-aspirations"
                        name="career_aspirations"
                        rows={3}
                        maxLength={500}
                        placeholder="Tell us about your career goals, preferred roles, location preferences..."
                        className="w-full px-4 py-3 rounded-xl bg-background-50 border border-background-200 text-foreground-900 text-sm placeholder:text-foreground-400 outline-none focus:border-accent-400 focus:ring-2 focus:ring-accent-100 transition-all resize-none"
                      ></textarea>
                      <p className="text-foreground-400 text-xs mt-1">Max 500 characters</p>
                    </div>

                    {/* Error message */}
                    {error && (
                      <div className="flex items-center gap-2 p-4 rounded-xl bg-red-50 border border-red-200 text-red-600 text-sm">
                        <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                          <i className="ri-error-warning-line"></i>
                        </span>
                        {error}
                      </div>
                    )}

                    {/* Submit */}
                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full inline-flex items-center justify-center gap-3 px-8 py-4 rounded-full bg-accent-500 text-background-50 font-semibold text-sm whitespace-nowrap hover:bg-accent-600 transition-all duration-300 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {submitting ? (
                        <>
                          <span className="w-5 h-5 flex items-center justify-center">
                            <i className="ri-loader-4-line animate-spin"></i>
                          </span>
                          Submitting...
                        </>
                      ) : (
                        <>
                          <span className="w-5 h-5 flex items-center justify-center">
                            <i className="ri-send-plane-fill text-sm"></i>
                          </span>
                          Submit Application
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <style>{`
        .sr-only-apply {
          position: absolute;
          width: 1px;
          height: 1px;
          padding: 0;
          margin: -1px;
          overflow: hidden;
          clip: rect(0, 0, 0, 0);
          white-space: nowrap;
          border-width: 0;
        }
      `}</style>

      <Footer />
    </main>
  );
}